
import * as React from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { addDays, format, subDays, subMonths, subYears } from 'date-fns';
import { DateRange } from 'react-day-picker';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface DateRangeFilterProps {
  dateRange: DateRange;
  setDateRange: (dateRange: DateRange) => void;
  className?: string;
}

export function DateRangeFilter({
  dateRange,
  setDateRange,
  className,
}: DateRangeFilterProps) {
  const handleSelectPreset = (value: string) => {
    const now = new Date();
    switch (value) {
      case 'last_7_days':
        setDateRange({ from: subDays(now, 7), to: now });
        break;
      case 'last_30_days':
        setDateRange({ from: subDays(now, 30), to: now });
        break;
      case 'last_3_months':
        setDateRange({ from: subMonths(now, 3), to: now });
        break;
      case 'last_6_months':
        setDateRange({ from: subMonths(now, 6), to: now });
        break;
      case 'last_12_months':
         setDateRange({ from: subMonths(now, 12), to: now });
        break;
      case 'this_year':
        setDateRange({ from: new Date(now.getFullYear(), 0, 1), to: now });
        break;
      case 'last_year':
        const lastYear = subYears(now, 1);
        setDateRange({ from: new Date(lastYear.getFullYear(), 0, 1), to: new Date(lastYear.getFullYear(), 11, 31) });
        break;
      case 'all_time':
        setDateRange({ from: subYears(now, 2), to: now });
        break;
    }
  };

  return (
    <div className={cn('grid gap-2 sm:flex sm:items-center sm:gap-4', className)}>
      <Select onValueChange={handleSelectPreset}>
        <SelectTrigger className="w-full sm:w-[180px]">
          <SelectValue placeholder="Select period" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="last_7_days">Last 7 days</SelectItem>
          <SelectItem value="last_30_days">Last 30 days</SelectItem>
          <SelectItem value="last_3_months">Last 3 months</SelectItem>
          <SelectItem value="last_6_months">Last 6 months</SelectItem>
          <SelectItem value="last_12_months">Last 12 months</SelectItem>
          <SelectItem value="this_year">This year</SelectItem>
          <SelectItem value="last_year">Last year</SelectItem>
          <SelectItem value="all_time">All time</SelectItem>
        </SelectContent>
      </Select>
      
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={'outline'}
            className={cn(
              'w-full sm:w-[300px] justify-start text-left font-normal',
              !dateRange && 'text-muted-foreground'
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {dateRange?.from ? (
              dateRange.to ? (
                <>
                  {format(dateRange.from, 'LLL dd, y')} -{' '}
                  {format(dateRange.to, 'LLL dd, y')}
                </>
              ) : (
                format(dateRange.from, 'LLL dd, y')
              )
            ) : (
              <span>Pick a date</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={dateRange?.from}
            selected={dateRange}
            onSelect={(range) => range && setDateRange(range)}
            numberOfMonths={2}
            disabled={(date) => date > new Date()} // Disable future dates
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
